import { Injectable } from '@angular/core';
import { createAction, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Assign, ValuesType } from 'utility-types';
import { BannerOptions } from '../../banner/banner.component';
import { CheckboxGroupOptions } from '../../checkbox-group/checkbox-group.component';
import { CheckBoxOptions } from '../../checkbox/checkbox.component';
import { BannerID, BannerModel, CheckboxGroupModel, CheckBoxModel, ComponentID, ComponentModel, FieldsetID, FieldsetModel, InfoID, InfoModel, LabelID, LabelModel, ModalModel, ModalOpenerModel, OptionID, OptionModel, ReviewID, ReviewModel, SelectModel, ServiceStepID, ServiceStepModel, StepID, StepModel, TextInputModel } from '../../form-json.types';
import { InfoOptions } from '../../info/info.component';
import { ModalOpenerOptions } from '../../modal-opener/modal-opener.component';
import { OptionSetting } from '../../options/options.component';
import { ReviewOptions } from '../../review/review.component';
import { SelectOptions } from '../../select/select.component';
import { FieldsetOptions } from '../../step/step.component';
import { State } from '../../store';
import { selectPath } from '../../store/form';
import { TextInputOptions } from '../../text-input/text-input.component';
import { ConditionService } from '../condition/condition.service';
import { DataService } from '../data/data.service';
import { FormCoreModule } from '../form-core.module';

export const updateView = createAction('updateView')

@Injectable({
  providedIn: FormCoreModule
})
export class ParserService {

  get(stepid: string): Observable<FieldsetOptions[]> {
    const stepModel = this.data.getModel<StepModel | ServiceStepModel>(this.data.getSteps().find(x => x.id === stepid) as StepID | ServiceStepID)
    const fieldsetsModel: ValuesType<Pick<Data, 'fieldsetsModel'>> = {}
    const componentsModel: ValuesType<Pick<Data, 'componentsModel'>> = {}
    const labelsModel: ValuesType<Pick<Data, 'labelsModel'>> = {}
    const optionsModel: ValuesType<Pick<Data, 'optionsModel'>> = {}
    const data = { stepModel, fieldsetsModel, componentsModel, labelsModel, optionsModel }
    stepModel.relationships.fieldsets.data.forEach(fieldset => this.setModel(fieldset, data))
    for (let id in fieldsetsModel) {
      fieldsetsModel[id].relationships.components?.data?.forEach(component => this.setModel(component, data))
    }
    const presences$: Observable<any>[] = []
    for (let id in componentsModel) {
      const component = componentsModel[id]
      if (component.type !== 'intro-component' && component.type !== 'info-component') {
        if (component.type !== 'checkbox-group-component') this.setModel(component.relationships.label.data, data)
        if (component.type !== 'review-component' && component.relationships['components-to-reset']?.data?.length) {
          component.relationships['components-to-reset'].data.some(toBeReset => {
            switch (toBeReset.type) {
              case 'fieldset':
                if (!!fieldsetsModel[toBeReset.id]) {
                  presences$.push(this.store.select(selectPath(component.attributes.path)))
                  return true
                }
                return false
              default:
                if (!!componentsModel[toBeReset.id]) {
                  presences$.push(this.store.select(selectPath(component.attributes.path)))
                  return true
                }
                return false
            }
          })
        }
      }
    }
    return presences$.length > 0 ? combineLatest(presences$).pipe(map(() => this.generate(data))) : of(this.generate(data))
  }

  constructor(private store: Store<State>, private data: DataService, private condition: ConditionService) { }

  private parseTextInput(textInput: TextInputModel): Assign<Omit<TextInputOptions, 'position' | 'label' | 'formControl'>, { inline: boolean }> {
    const { id, type, attributes: { path, placeholder, inline, keyboard, prefix, required, maxlength, disabled }, relationships: { presences } } = textInput
    const presence = !presences?.data?.length || presences.data.every(value => this.condition.assign(value))
    return { id, type, path, placeholder, inline: inline || false, keyboard: keyboard || 'text', prefix: prefix || '', required: required || false, maxlength, disabled: disabled || !presence, presence }
  }

  private setModel(_item: FieldsetID | ComponentID | LabelID | BannerID | InfoID | ReviewID, data: Data) {
    const item = this.data.getModel(_item) as FieldsetModel | ComponentModel | LabelModel | BannerModel | InfoModel | ReviewModel
    switch (item.type) {
      case 'field-label':
        data.labelsModel[item.id] = item
        break
      case 'fieldset':
        data.fieldsetsModel[item.id] = item
        break
      case 'text-component':
        data.componentsModel[item.id] = item
        break
      case 'select-component':
        data.componentsModel[item.id] = item
        break
      case 'intro-component':
        data.componentsModel[item.id] = item
        break
      case 'info-component':
        data.componentsModel[item.id] = item
        break
      case 'checkbox-group-component':
        data.componentsModel[item.id] = item
        break
      case 'review-component':
        data.componentsModel[item.id] = item
    }
  }

  private generate(data: Data): FieldsetOptions[] {
    const { stepModel, fieldsetsModel, componentsModel, labelsModel } = data
    return stepModel.relationships.fieldsets.data.map(fieldset => {
      const { id, type, attributes: { legend, classes }, relationships: { components, presences } } = fieldsetsModel[fieldset.id]
      const opener = fieldsetsModel[fieldset.id].relationships['info-opener']?.data
      return {
        id, type, classes, opener: !!opener ? this.parseOpener(this.data.getModel<ModalOpenerModel>(opener)) : undefined, presence: !presences?.data?.length || presences.data.every(value => this.condition.assign(value)), legend, components: !components?.data ? [] : components.data.map(component => {
          const componentModel = componentsModel[component.id]
          switch (componentModel.type) {
            case 'text-component':
              return { ...this.parseTextInput(componentModel), label: labelsModel[componentModel.relationships.label.data.id].attributes.text, formControl: this.data.getControl(componentModel.attributes.path) }
            case 'select-component':
              return { ...this.parseSelect(componentModel), label: labelsModel[componentModel.relationships.label.data.id].attributes.text, formControl: this.data.getControl(componentModel.attributes.path) }
            case 'info-component':
              return { ...this.parseInfo(componentModel) }
            case 'intro-component':
              return { ...this.parseBanner(componentModel) }
            case 'checkbox-group-component':
              return { ...this.parseCheckboxGroup(componentModel), formControl: this.data.getControl(componentModel.attributes.path) }
            case 'review-component':
              return { ...this.parseReview(componentModel), label: labelsModel[componentModel.relationships.label.data.id].attributes.text }
          }
        })
      }
    })
  }

  private parseReview(review: ReviewModel): Assign<Omit<ReviewOptions, 'position' | 'label'>, {inline: boolean}> {
    const { id, type, attributes: { inline, editable }, relationships: { components, fieldset, presences } } = review
    const component = this.data.getModel<ComponentModel>(components.data[0])
    const url = '/step/' + this.data.getModel<FieldsetModel>(component.relationships.fieldset.data).relationships.step.data.id
    const presence = !presences?.data?.length || presences.data.every(value => this.condition.assign(value))
    const path = component.attributes.path
    const value = this.getValue(component)
    return { id, type, inline: inline || false, url, presence, path, value, editable: editable === undefined || editable }
  }

  private getValue(component: ComponentModel) {
    switch (component.type) {
      case 'checkbox-group-component':
        return this.getCheckBoxLabel(component)
      case 'select-component':
        return this.getSelectText(component)
      case 'text-component':
        return this.data.getControl(component.attributes.path).value
    }
  }

  private getSelectText(select: SelectModel) {
    const value = this.data.getControl(select.attributes.path).value
    const options = select.relationships.options.data.map(option => this.data.getModel<OptionModel>(option))
    return options.find(option => option.attributes.value === value)?.attributes.text
  }

  private getCheckBoxLabel(checkboxGroup: CheckboxGroupModel) {
    const value = this.data.getControl(checkboxGroup.attributes.path).value as Array<any>
    const checkboxes = checkboxGroup.relationships.components.data.map(checkbox => this.data.getModel<CheckBoxModel>(checkbox))
    if (value.length == 0) return ''
    return checkboxes.filter(checkbox => value.indexOf(checkbox.attributes.value) > -1).map(checkbox => this.data.getModel<LabelModel>(checkbox.relationships.label.data).attributes.text).join()
  }

  private parseInfo(info: InfoModel): InfoOptions {
    const { id, type, attributes: { entry }, relationships } = info
    const opener = relationships['info-opener']?.data && this.data.getModel<ModalOpenerModel>(relationships['info-opener'].data)
    return { id, type, information: entry, opener: opener && this.parseOpener(opener), presence: !relationships.presences?.data?.length || relationships.presences.data.every(value => this.condition.assign(value)) }
  }

  private parseCheckboxGroup(checkboxGroup: CheckboxGroupModel): Omit<CheckboxGroupOptions, 'formControl'> {
    const { id, type, relationships: { components, presences }, attributes: { path, disabled } } = checkboxGroup
    const presence = !presences?.data?.length || presences.data.every(value => this.condition.assign(value))
    return { id, type, disabled: disabled || !presences, presence, path, checkboxes: components.data.map(checkbox => this.parseCheckBox(this.data.getModel<CheckBoxModel>(checkbox))) }
  }

  private parseSelect(select: SelectModel): Assign<Omit<SelectOptions, 'position' | 'label' | 'formControl'>, { inline: boolean }> {
    const { id, type, attributes: { path, placeholder, inline, required, disabled }, relationships: { presences } } = select
    const presence = !presences?.data?.length || presences.data.every(value => this.condition.assign(value))
    const options = this.parseOptions(select.relationships.options.data)
    return { id, type, path, placeholder, inline: inline || false, required: required || false, options, disabled: disabled || !presence, presence }
  }

  private parseCheckBox(checkbox: CheckBoxModel): CheckBoxOptions {
    const { id, type, attributes: { value }, relationships: { label, presences } } = checkbox
    const presence = !presences?.data?.length || presences.data.every(value => this.condition.assign(value))
    return { id, type, value, label: this.data.getModel<LabelModel>(label.data).attributes.text, presence }
  }

  private parseOptions(options: OptionID[]): OptionSetting[] {
    return options.map(option => {
      const { attributes: { text, value }, relationships } = this.data.getModel<OptionModel>(option)
      const presence = !relationships?.presences?.data || relationships.presences.data.every(value => this.condition.assign(value))
      return { text, value, presence }
    })
  }

  private parseBanner(banner: BannerModel): BannerOptions {
    const { id, type, attributes: { text, title, img }, relationships } = banner
    const opener = relationships['info-opener']?.data && this.data.getModel<ModalOpenerModel>(relationships['info-opener'].data)
    return { id, type, text, title, requirement: banner.attributes['requirements-text'], opener: opener && this.parseOpener(opener), image: img }
  }

  private parseOpener(opener: ModalOpenerModel): ModalOpenerOptions {
    const { attributes: { label }, relationships: { modal: { data } } } = opener
    const { type, attributes: { title, content } } = this.data.getModel<ModalModel>(data)
    const subTitle = this.data.getModel<ModalModel>(data).attributes['sub-title']
    return { label, text: opener.attributes['label-text'], modal: { title, content, 'sub-title': subTitle, type } }
  }

}

interface Data {
  stepModel: StepModel | ServiceStepModel,
  fieldsetsModel: {
    [key: string]: FieldsetModel
  },
  componentsModel: {
    [key: string]: ComponentModel | BannerModel | InfoModel | ReviewModel
  },
  labelsModel: {
    [key: string]: LabelModel
  },
  optionsModel: {
    [key: string]: OptionModel
  }
}